import { lazy } from 'react';
import { matchPath } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  FileSearch,
  Landmark,
  Settings,
  Users,
  UserCog,
  Activity,
  Layers,
  Wrench,
} from 'lucide-react';

// Fuente de verdad del sistema de ayuda contextual. Cada tema hereda la
// restricción de acceso de la pantalla que documenta: si cambia la protección
// de una ruta en App.jsx, hay que actualizar aquí su `restriccion`.
export const HELP_TOPICS = [
  {
    id: 'dashboard',
    titulo: 'Dashboard',
    descripcion: 'Tu radar de oportunidades: el resumen diario de lo que el sistema ha encontrado por ti.',
    icon: LayoutDashboard,
    seccion: 'General',
    match: ['/dashboard'],
    restriccion: {},
    load: () => import('./topics/AyudaDashboard'),
  },
  {
    id: 'detalle-subasta',
    titulo: 'Ficha de subasta',
    descripcion: 'Qué significa cada dato de la ficha y de dónde lo ha sacado la inteligencia artificial.',
    icon: FileSearch,
    seccion: 'General',
    match: ['/subastas/:id'],
    restriccion: {},
    load: () => import('./topics/AyudaDetalleSubasta'),
  },
  {
    id: 'subastas',
    titulo: 'Explorador de subastas',
    descripcion: 'Cómo buscar, filtrar y marcar como favoritas las subastas que interesan.',
    icon: ClipboardList,
    seccion: 'General',
    match: ['/subastas'],
    restriccion: {},
    load: () => import('./topics/AyudaSubastas'),
  },
  {
    id: 'remanentes',
    titulo: 'Remanentes',
    descripcion: 'Sobrantes de subastas y depósitos judiciales: qué son y cómo localizar a sus beneficiarios.',
    icon: Landmark,
    seccion: 'General',
    match: ['/remanentes', '/remanentes/beneficiario/:beneficiario', '/remanentes/:id'],
    restriccion: { permiso: 'remanentes' },
    load: () => import('./topics/AyudaRemanentes'),
  },
  {
    id: 'config',
    titulo: 'Configuración IA',
    descripcion: 'Los criterios de caza de tu grupo: qué le pides a la IA que busque y cómo puntúa cada subasta.',
    icon: Settings,
    seccion: 'Gestión',
    match: ['/config'],
    restriccion: { roles: ['admin', 'superadmin', 'boss'] },
    load: () => import('./topics/AyudaConfig'),
  },
  {
    id: 'equipo',
    titulo: 'Equipo',
    descripcion: 'Invitar y gestionar a los miembros de tu grupo de trabajo.',
    icon: Users,
    seccion: 'Gestión',
    match: ['/equipo'],
    restriccion: { roles: ['admin', 'superadmin', 'boss'] },
    load: () => import('./topics/AyudaEquipo'),
  },
  {
    id: 'usuarios',
    titulo: 'Usuarios',
    descripcion: 'Administración global de cuentas, roles y permisos de toda la plataforma.',
    icon: UserCog,
    seccion: 'Administración',
    match: ['/usuarios', '/usuarios/:id'],
    restriccion: { roles: ['admin', 'superadmin'] },
    load: () => import('./topics/AyudaUsuarios'),
  },
  {
    id: 'torre-de-control',
    titulo: 'Torre de Control',
    descripcion: 'La sala de máquinas: estado del rastreador, logs, métricas y versiones del sistema.',
    icon: Activity,
    seccion: 'Administración',
    match: ['/logs'],
    restriccion: { roles: ['admin', 'superadmin'] },
    load: () => import('./topics/AyudaTorreControl'),
  },
  {
    id: 'ingesta',
    titulo: 'Ingesta L0-L2',
    descripcion: 'El viaje de un edicto: del documento oficial en bruto a los datos estructurados que ves en pantalla.',
    icon: Layers,
    seccion: 'Administración',
    match: ['/ingesta'],
    restriccion: { roles: ['admin', 'superadmin'] },
    load: () => import('./topics/AyudaIngesta'),
  },
  {
    id: 'setup',
    titulo: 'Setup',
    descripcion: 'Infraestructura de acceso a las fuentes: certificado del BOE, proxies y modelos de IA.',
    icon: Wrench,
    seccion: 'Administración',
    match: ['/setup'],
    restriccion: { roles: ['admin', 'superadmin'] },
    load: () => import('./topics/AyudaSetup'),
  },
];

// El componente lazy se crea una única vez a nivel de módulo; crearlo en
// render remontaría el árbol en cada actualización.
for (const topic of HELP_TOPICS) {
  topic.Component = lazy(topic.load);
}

export const HELP_SECCIONES = ['General', 'Gestión', 'Administración'];

// Resuelve el tema de ayuda de la pantalla actual. Solo mira pathname:
// /subastas?interesante=true (Favoritos) resuelve al tema "subastas", que es
// la misma pantalla filtrada.
export function findTopicForLocation(location) {
  for (const topic of HELP_TOPICS) {
    for (const pattern of topic.match) {
      if (matchPath({ path: pattern, end: true }, location.pathname)) {
        return topic;
      }
    }
  }
  return null;
}

// Misma regla que ProtectedRoute: lista de roles y/o permiso granular.
// hasPermiso ya concede el permiso a admin/superadmin (AuthContext).
export function canViewTopic(topic, user, hasPermiso) {
  const { roles = [], permiso = null } = topic.restriccion || {};
  if (roles.length > 0 && !roles.includes(user?.rol)) return false;
  if (permiso && !hasPermiso(permiso)) return false;
  return true;
}

export function audienciaDeTema(topic) {
  const { roles = [], permiso = null } = topic.restriccion || {};
  if (permiso) return 'Usuarios con permiso de Remanentes';
  if (roles.length > 0) {
    return roles.includes('boss') ? 'Boss y administradores' : 'Solo administradores';
  }
  return 'Todos los usuarios';
}
